import { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
  Icon,
  InputAdornment,
  Paper,
  useTheme,
} from '@mui/material';
import { GoogleSearchResult, LocalSearchResult, Sentiment } from '../models/restaurant';
import api from '../api';
import CertifiedBadge from './CertifiedBadge';

interface RestaurantSearchBarProps {
  onRestaurantAdded: () => void;
}

const normalizeAddress = (address: string): string => {
  return address
    .toLowerCase()
    .replace(/\broad\b/g, 'rd')
    .replace(/\bstreet\b/g, 'st')
    .replace(/\bavenue\b/g, 'ave')
    .replace(/\bdrive\b/g, 'dr')
    .replace(/\blane\b/g, 'ln')
    .replace(/\bboulevard\b/g, 'blvd')
    .replace(/\bplace\b/g, 'pl')
    .replace(/\bcourt\b/g, 'ct')
    .replace(/\bsquare\b/g, 'sq')
    .replace(/\bterrace\b/g, 'ter')
    .replace(/\bclose\b/g, 'cl')
    .replace(/[^a-z0-9]/g, '');
};

const addressesMatch = (addr1: string, addr2: string): boolean => {
  const norm1 = normalizeAddress(addr1);
  const norm2 = normalizeAddress(addr2);
  if (!norm1 || !norm2) return false;
  if (norm1 === norm2) return true;
  return norm1.includes(norm2) || norm2.includes(norm1);
};

const deduplicateLocalResults = (
  results: LocalSearchResult[],
): LocalSearchResult[] => {
  const unique: LocalSearchResult[] = [];
  for (const result of results) {
    const addr = result.addressFull || result.address || '';
    const existingIdx = unique.findIndex((u) =>
      addressesMatch(u.addressFull || u.address || '', addr),
    );
    if (existingIdx === -1) {
      unique.push(result);
    } else if (result.isCertified && !unique[existingIdx].isCertified) {
      unique[existingIdx] = result;
    }
  }
  return unique;
};

const RestaurantSearchBar = ({ onRestaurantAdded }: RestaurantSearchBarProps) => {
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const [localResults, setLocalResults] = useState<LocalSearchResult[]>([]);
  const [googleResults, setGoogleResults] = useState<GoogleSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchingGoogle, setIsSearchingGoogle] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isAdding, setIsAdding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (query.length < 2) {
      setLocalResults([]);
      setGoogleResults([]);
      setError(null);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setIsLoading(true);
      setIsSearchingGoogle(false);
      setError(null);
      setLocalResults([]);
      setGoogleResults([]);

      let local: LocalSearchResult[] = [];
      let filteredGoogle: GoogleSearchResult[] = [];

      try {
        const localData = await api.restaurant.searchLocal(query);
        const rawLocal = Array.isArray(localData) ? localData : [];
        local = deduplicateLocalResults(rawLocal);
        setLocalResults(local);
      } catch (err) {
        console.error('Database search failed:', err);
      }

      setIsLoading(false);
      setIsSearchingGoogle(true);

      try {
        const googleData = await api.restaurant.searchFromGoogle(query);
        const google = Array.isArray(googleData) ? googleData : [];

        filteredGoogle = google.filter((g) => {
          const googleAddr = g.address || '';
          if (!googleAddr) return true;
          return !local.some((l) =>
            addressesMatch(l.addressFull || l.address || '', googleAddr),
          );
        });

        setGoogleResults(filteredGoogle);
      } catch (err) {
        console.error('Google search failed:', err);
      }

      setIsSearchingGoogle(false);

      if (local.length === 0 && filteredGoogle.length === 0) {
        setError('No restaurants found. Try a different search.');
      }
    }, 500);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectGoogle = async (result: GoogleSearchResult) => {
    setIsAdding(result.googlePlaceId);
    try {
      const response = await api.restaurant.createFromGoogle(
        result.googlePlaceId,
      );
      if (response && response.id) {
        setQuery('');
        setLocalResults([]);
        setGoogleResults([]);
        setIsOpen(false);
        onRestaurantAdded();
      } else {
        setError('Failed to add restaurant. Please try again.');
      }
    } catch (err) {
      console.error('Failed to add restaurant from Google:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to add restaurant. Please try again.';
      setError(errorMessage);
    } finally {
      setIsAdding(null);
    }
  };

  const handleSelectLocal = async (result: LocalSearchResult) => {
    if (!result.id) return;
    setIsAdding(result.id);
    try {
      const response = await api.sentiment.create({
        restaurantId: result.id,
        sentiment: Sentiment.KeenToGo,
      });
      if (response) {
        setQuery('');
        setLocalResults([]);
        setGoogleResults([]);
        setIsOpen(false);
        onRestaurantAdded();
      } else {
        setError('Failed to add restaurant to Keen list. Please try again.');
      }
    } catch (err) {
      console.error('Failed to add restaurant from local:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to add restaurant. Please try again.';
      setError(errorMessage);
    } finally {
      setIsAdding(null);
    }
  };

  const hasResults = localResults.length > 0 || googleResults.length > 0;
  const isSearching = isLoading || isSearchingGoogle;

  return (
    <Box
      ref={containerRef}
      sx={{ position: 'relative', width: '100%', mb: 2 }}
    >
      <TextField
        fullWidth
        placeholder="Search for restaurants..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon sx={{ color: theme.palette.grey[500] }}>search</Icon>
            </InputAdornment>
          ),
          endAdornment: isSearching ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
          },
        }}
      />

      {isOpen &&
        (query.length >= 2 || hasResults || error) && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            mt: 0.5,
            maxHeight: 400,
            overflow: 'auto',
            borderRadius: 2,
          }}
        >
          {error && !hasResults && !isSearching && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {error}
              </Typography>
            </Box>
          )}

          {isLoading && !hasResults && (
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={16} />
            </Box>
          )}

          {hasResults && (
            <List sx={{ py: 0 }}>
              {localResults.map((result) => (
                <ListItem
                  key={`local-${result.id}`}
                  onClick={() => !isAdding && handleSelectLocal(result)}
                  sx={{
                    cursor: isAdding ? 'wait' : 'pointer',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    opacity:
                      isAdding && isAdding !== result.id ? 0.5 : 1,
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Typography fontWeight={500}>
                          {result.title}
                        </Typography>
                        {result.isCertified && <CertifiedBadge size={18} />}
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                      >
                        {result.address}
                      </Typography>
                    }
                  />
                  {isAdding === result.id ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Icon sx={{ color: theme.palette.success.main }}>
                      add_circle
                    </Icon>
                  )}
                </ListItem>
              ))}

              {googleResults.map((result) => (
                <ListItem
                  key={`google-${result.googlePlaceId}`}
                  onClick={() => !isAdding && handleSelectGoogle(result)}
                  sx={{
                    cursor: isAdding ? 'wait' : 'pointer',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    opacity:
                      isAdding && isAdding !== result.googlePlaceId
                        ? 0.5
                        : 1,
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Typography fontWeight={500}>
                          {result.title}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                      >
                        {result.address}
                      </Typography>
                    }
                  />
                  {isAdding === result.googlePlaceId ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Icon sx={{ color: theme.palette.success.main }}>
                      add_circle
                    </Icon>
                  )}
                </ListItem>
              ))}
            </List>
          )}
          {!error &&
            query.length >= 2 &&
            !isSearching &&
            !hasResults && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No restaurants found
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default RestaurantSearchBar;
